// =============================================================================
// domain/demo.ts — the sample book, generated.
// -----------------------------------------------------------------------------
// Relocated from the old client's `buildDemoData` (app/index.html:8053-8312).
//
// ── 🚨 WHY THIS EXISTS AT ALL: IT IS A PROMISE ON THE LIVE PRICING PAGE ──
//
// "Pattern tagging + a full Demo account" is a FREE-tier bullet on the page
// selling the product right now (landing/index.html:857). The old client has
// one; this client had nothing — not on the Get Started screen, not in the
// shell, nowhere. Someone signs up partly on that basis and finds no demo.
// #90's second decision was to honour it rather than drop the claim.
//
// ── 🚨 THE ROWS ARE DATABASE-SHAPED, NOT DOMAIN-SHAPED, AND THAT IS THE POINT ──
//
// They go in exactly where a real read comes out, so `tradeFromDb` and every
// selector below it run on the demo book unchanged. Generating domain objects
// would have skipped the serializer — the one layer most likely to disagree with
// itself — and a demo that exercises a different code path from real data is a
// demo that can look right while the app is broken.
//
// ── 🚨 DETERMINISTIC, AND NOT AS A NICETY ──
//
// A seeded PRNG (mulberry32), so the same day always produces the same book.
// The old client's header says why: the numbers are TUNED. Each setup has its
// own win rate, R distribution, stop width, excursion depth and post-exit run,
// so the excursion charts show a real good-vs-bad spread and the Coach's Report
// finds leaks worth reading. A demo that reshuffles on every load cannot be
// tuned, screenshotted, or supported ("what am I looking at?").
//
// ⚠ TAKES `today` RATHER THAN CALLING THE CLOCK. Same reason `hasProAccess`
// takes `paidLaunch`: a generator that reaches for its own inputs can only be
// tested by faking the machine's clock. It also makes the 120-day window
// reproducible in a test that runs at midnight.
//
// ── ⚠ EVERY ID CARRIES THE PREFIX ──
//
// `__demo_`. Nothing here is ever written, but the prefix means a stray row is
// identifiable on sight rather than by inference — the old client leans on it
// for exactly one thing (keeping demo rows out of the free-tier counts), and
// that is a check, not a hope.
// =============================================================================

import { dateToISO, isoToDate } from './session';

export const DEMO_ACCOUNT_ID = '__demo_account__';
/** Every demo entity id starts with this. */
export const DEMO_PREFIX = '__demo_';

/** A row as the database would return it. */
export type DemoRow = Record<string, unknown>;

export interface DemoData {
  /** The funded account plus its eval history. */
  accounts: DemoRow[];
  /**
   * ⚠ ITS OWN TABLE, not a field on the account.
   *
   * `loadAccounts` reads `account_adjustments` in a second query and REFUSES
   * to return accounts without it — an account missing its "Reset fee" rows
   * reads as attempt 1 with the original drawdown, which is more room than the
   * trader has. Nesting them would have produced accounts with no fee history
   * and a prop report full of dashes.
   */
  adjustments: DemoRow[];
  patterns: DemoRow[];
  exitModels: DemoRow[];
  trades: DemoRow[];
}

/** ES: $50 a point, ~$2.10 a contract round-turn. */
const DOLLARS_PER_POINT = 50;
const FEE_PER_CONTRACT = 2.10;
/** How many trades the book holds. */
const DEMO_N = 150;
/** How far back the window reaches, in calendar days. */
const DEMO_DAYS = 120;
/** A fixed instant, so nothing in the book moves with the wall clock. */
const FIXED_TS = 1781000000000;
/**
 * ⚠ EVERY ROW CARRIES `updated_at`, and it is not decoration.
 *
 * It is the per-row concurrency guard, and the metrics read REFUSES a row
 * without it: "the projection asked for 12 columns and the server returned
 * fewer". Every reader coerces an absent column to 0/null, so the demo would
 * have produced a complete-looking set of WRONG numbers instead of an error.
 *
 * 🚨 Found by running the app's own reads against this book, not by inspection.
 * The generator looked finished and its own tests were green.
 */
const STAMP = new Date(FIXED_TS).toISOString();

// ---------------------------------------------------------------------------
// the generator's arithmetic
// ---------------------------------------------------------------------------

/**
 * mulberry32 — small, fast, and the same sequence everywhere.
 *
 * ⚠ Ported unchanged from the old client so both books are recognisably the
 * same shape. Not cryptographic and does not need to be.
 */
export function demoRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Round to an ES tick. */
const rt = (n: number): number => Math.round(n * 4) / 4;
const r2 = (n: number): number => Math.round(n * 100) / 100;
const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Per-setup behaviour, and every number here is doing a job.
 *
 * ⚠ THE LEAKS ARE DESIGNED IN. VWAP Reclaim leaves the most on the table
 * (`leave`) and keeps running after the exit (`extBias`) — an EXIT problem.
 * Liquidity Sweep's losers barely go favourable first (`favFirst` 0.25) — an
 * ENTRY problem. The Coach's Report is supposed to be able to tell them apart,
 * so the sample book has to contain both, distinguishable.
 *
 *   wr        win rate
 *   win/loss  realized R range (winners / losers)
 *   stop      planned risk range, ES points
 *   winMae    winner heat as a FRACTION of the stop (shallow = slack to tighten)
 *   loseMae   loser overshoot as a fraction of the stop (>1 = blew through)
 *   leave     how far winners ran BEYOND the exit, as a fraction of the stop
 *   loserMfe  how far a favourable-first loser reached
 *   hold      duration range in SECONDS — the setup's tempo
 *   favFirst  P(a loser went favourable first)
 *   extBias   scales post-exit continuation
 *   w         selection weight
 */
interface Profile {
  sfx: string; name: string; cat: string; w: number; wr: number;
  win: [number, number]; loss: [number, number]; stop: [number, number];
  winMae: [number, number]; loseMae: [number, number]; leave: [number, number];
  loserMfe: [number, number]; hold: [number, number];
  favFirst: number; extBias: number;
}

const PROFILES: readonly Profile[] = [
  { sfx: 'trend', name: 'Trend Pullback', cat: 'Continuation', w: 30, wr: 0.60, win: [1.3, 2.1], loss: [0.9, 1.1], stop: [4, 6], winMae: [0.25, 0.50], loseMae: [1.0, 1.3], leave: [0.10, 0.35], loserMfe: [0.5, 1.4], hold: [300, 720], favFirst: 0.70, extBias: 0.5 },
  { sfx: 'fbd', name: 'Failed Breakdown', cat: 'Reversal', w: 22, wr: 0.58, win: [1.3, 2.0], loss: [0.9, 1.05], stop: [3, 5], winMae: [0.30, 0.55], loseMae: [1.0, 1.25], leave: [0.15, 0.40], loserMfe: [0.4, 1.2], hold: [150, 420], favFirst: 0.65, extBias: 0.6 },
  { sfx: 'vwap', name: 'VWAP Reclaim', cat: 'Reversal', w: 20, wr: 0.50, win: [1.2, 2.0], loss: [0.95, 1.15], stop: [6, 8], winMae: [0.55, 0.85], loseMae: [1.0, 1.3], leave: [0.80, 1.60], loserMfe: [0.6, 1.6], hold: [840, 1500], favFirst: 0.70, extBias: 1.4 },
  { sfx: 'orb', name: 'Opening Range Break', cat: 'Breakout', w: 16, wr: 0.45, win: [1.1, 1.8], loss: [1.0, 1.25], stop: [5, 7], winMae: [0.40, 0.70], loseMae: [1.2, 1.6], leave: [0.50, 1.00], loserMfe: [0.2, 0.7], hold: [40, 140], favFirst: 0.35, extBias: 0.9 },
  { sfx: 'sweep', name: 'Liquidity Sweep', cat: 'Reversal', w: 12, wr: 0.38, win: [1.1, 1.7], loss: [1.1, 1.4], stop: [4, 6], winMae: [0.40, 0.65], loseMae: [1.3, 1.7], leave: [0.20, 0.50], loserMfe: [0.1, 0.4], hold: [45, 160], favFirst: 0.25, extBias: 0.5 },
];

const EXIT_MODELS: readonly [string, string][] = [
  ['em_fixed', 'Fixed 1:2 Target'],
  ['em_runner', 'Scale-out + Runner'],
  ['em_trail', 'VWAP Trail'],
];

const CONFLUENCES = ['key-levels', 'multi-vwap', 'volume-profile', 'orderflow', 'icebergs', 'gex-dex'];
const SESSIONS = ['Pre-market', 'Open', 'Morning', 'Lunch', 'Afternoon'];
const MOODS = ['calm', 'focused', 'neutral', 'rushed', 'frustrated'];

/**
 * The ES quarterly front month for a date.
 *
 * ⚠ Rolls ~8 days before the third Friday of Mar/Jun/Sep/Dec, which is where ES
 * liquidity actually moves. It matters because `contract_full` is what the chart
 * uses to pull the EXACT contract trading that day rather than a continuous
 * proxy — the same thing that made real MAE/MFE wrong near a roll.
 */
export function esContractFor(iso: string): string {
  const Q: readonly [number, string][] = [[2, 'H'], [5, 'M'], [8, 'U'], [11, 'Z']];
  const thirdFriday = (yr: number, m0: number): Date => {
    const off = (5 - new Date(yr, m0, 1).getDay() + 7) % 7;
    return new Date(yr, m0, 1 + off + 14);
  };
  const dt = isoToDate(iso);
  for (let yr = dt.getFullYear(); yr <= dt.getFullYear() + 1; yr++) {
    for (const [m0, code] of Q) {
      const roll = thirdFriday(yr, m0);
      roll.setDate(roll.getDate() - 8);
      if (dt < roll) return `ES${code}${String(yr).slice(-2)}`;
    }
  }
  return `ESH${String(dt.getFullYear() + 1).slice(-2)}`;
}

// ---------------------------------------------------------------------------
// the book
// ---------------------------------------------------------------------------

let cache: { key: string; data: DemoData } | null = null;

/**
 * The whole sample book, as database rows.
 *
 * ⚠ MEMOISED ON `today`, and callers get a DEEP COPY. The old client learned
 * this the hard way: it handed out the cached arrays directly, so the first
 * screen that recomputed anything mutated the cache in place and the "identical
 * every load" guarantee quietly became false.
 */
export function buildDemoData(today: string): DemoData {
  if (cache?.key === today) return structuredClone(cache.data) as DemoData;
  const data = generate(today);
  cache = { key: today, data };
  return structuredClone(data) as DemoData;
}

function generate(today: string): DemoData {
  const rng = demoRng(20260612);
  const pick = <T>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)] as T;
  const plo = (r: readonly [number, number]): number => r[0] + rng() * (r[1] - r[0]);

  const patterns: DemoRow[] = PROFILES.map((p) => ({
    id: DEMO_PREFIX + 'pat_' + p.sfx,
    name: p.name,
    category: p.cat,
    description: '',
    criteria: '',
    status: 'production',
    created_at: STAMP,
    updated_at: STAMP,
  }));

  const exitModels: DemoRow[] = EXIT_MODELS.map(([sfx, name]) => ({
    id: DEMO_PREFIX + sfx,
    name,
    description: '',
    created_at: STAMP,
    updated_at: STAMP,
  }));

  // ⚠ A DETERMINISTIC QUOTA PER SETUP, then a shuffle. Dealing outcomes at
  // random would let small-sample noise wash out the very leaks the profiles
  // encode — the blend has to land where it was designed to (~54%).
  const totW = PROFILES.reduce((s, p) => s + p.w, 0);
  const plan: { prof: Profile; isWin: boolean }[] = [];
  for (const p of PROFILES) {
    const cnt = Math.max(6, Math.round(DEMO_N * p.w / totW));
    const nWin = Math.round(cnt * p.wr);
    for (let i = 0; i < cnt; i++) plan.push({ prof: p, isWin: i < nWin });
  }
  for (let i = plan.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = plan[i]!; plan[i] = plan[j]!; plan[j] = tmp;
  }

  const todayDate = isoToDate(today);
  // Trading days in the window, oldest → newest. Weekends skipped.
  const days: Date[] = [];
  for (let d = DEMO_DAYS; d >= 0; d--) {
    const day = new Date(todayDate);
    day.setDate(day.getDate() - d);
    if (day.getDay() !== 0 && day.getDay() !== 6) days.push(day);
  }

  const trades: DemoRow[] = [];
  let base = 5920;
  let tradeNo = 0;
  let planIdx = 0;

  // 🚨 THE PLAN IS PACED ACROSS THE WHOLE WINDOW. The old client's first version
  // dealt a flat 1-3 trades a day from the oldest end and stopped when the plan
  // ran out — about 74 trading days in — leaving the most recent three weeks
  // empty. Because the window slides daily, that dead tail was always "the last
  // couple of weeks": exactly what anyone opening the demo looks at first.
  for (let di = 0; di < days.length && planIdx < plan.length; di++) {
    const day = days[di]!;
    base = rt(base + (rng() - 0.48) * 22); // a slight upward drift
    const planLeft = plan.length - planIdx;
    const daysLeft = days.length - di;
    // Self-correcting rate: falling behind speeds up, running ahead slows down.
    // The jitter stops it looking metronomic, and an occasional zero-trade day
    // is realistic rather than a gap.
    let nTrades = Math.max(0, Math.round(planLeft / daysLeft + (rng() - 0.5) * 1.6));
    // Always leave one for a later day while days remain, so the plan cannot be
    // exhausted early and re-open that tail...
    if (daysLeft > 1) nTrades = Math.min(nTrades, planLeft - 1);
    // ...and give the last trading day the remainder, so there is ALWAYS recent
    // activity — today, or Friday when today is a weekend.
    else nTrades = planLeft;

    for (let k = 0; k < nTrades && planIdx < plan.length; k++) {
      tradeNo++;
      const dateStr = dateToISO(day);
      const { prof, isWin } = plan[planIdx++]!;

      const long = rng() < 0.5;
      const qty = pick([1, 1, 1, 2, 2]); // mostly 1-lot → believable exposure
      const riskPts = rt(plo(prof.stop));
      const rr = isWin ? r2(plo(prof.win)) : -r2(plo(prof.loss));
      const realizedPts = rt(Math.abs(rr) * riskPts) * (isWin ? 1 : -1);
      const entry = rt(base + (rng() - 0.5) * 12);
      const exit = rt(long ? entry + realizedPts : entry - realizedPts);
      const points = r2(long ? exit - entry : entry - exit);
      const fees = r2(FEE_PER_CONTRACT * qty);
      const grossPnl = r2(points * DOLLARS_PER_POINT * qty);
      const netPnl = r2(grossPnl - fees);

      // 🚨 THE LOSER'S SHAPE IS THE DIAGNOSIS. A loser that ran favourable first
      // is an EXIT problem; one that went straight to its stop is an ENTRY
      // problem. Winners are always favourable-first. This is what Loser
      // Asymmetry, E-Ratio, give-back and MFE capture all read.
      const favFirst = isWin || rng() < prof.favFirst;
      const mfePts = isWin
        ? r2(Math.abs(points) + riskPts * plo(prof.leave))
        : r2(riskPts * (favFirst ? plo(prof.loserMfe) : 0.03 + rng() * 0.12));
      const maePts = isWin ? r2(riskPts * plo(prof.winMae)) : r2(riskPts * plo(prof.loseMae));

      // Post-exit extension, across every window the Trade Extension card reads.
      // Monotonic: a longer window's peak contains a shorter one's.
      const extReason = !isWin ? 'stop' : rr >= 1.8 ? 'target' : 'discretionary';
      // ⚠ Stopped-first is keyed to the exit reason, so the demo reproduces the
      // divergence a real stop-qualified walk produces: a STOP exit sits on its
      // stop, so holding past it would mostly re-stop; a TARGET exit is far from
      // it. Every branch spends exactly one rng() call — an unbalanced branch
      // would make the whole book downstream depend on the outcome.
      const wouldStop = extReason === 'stop' ? rng() < 0.8
        : extReason === 'target' ? rng() < 0.12
        : rng() < 0.3;
      const eb = prof.extBias;
      const ext15 = wouldStop
        ? r2(-0.2 + rng() * 0.5)
        : r2((rng() < 0.4 ? -0.4 + rng() * 0.8 : 0.3 + rng() * 1.0) * eb);
      const ext30 = wouldStop ? r2(ext15 + rng() * 0.15) : r2(ext15 + rng() * 0.6 * eb);
      const ext60 = wouldStop ? ext30 : r2(ext30 + rng() * 0.8 * eb);
      const extSe = wouldStop ? ext30 : r2(ext60 + rng() * 1.2 * eb);
      // The break-even lens: a stop trailed to entry is tighter, so it stops
      // first MORE often and caps the peak lower. ⚠ The roll is the LEFT operand
      // of `||` so it always evaluates — a short-circuit here would spend a
      // different number of rng() calls per trade and break determinism.
      const beStop = rng() < (extReason === 'target' ? 0.2 : 0.45) || wouldStop;
      const beScale = 0.45 + rng() * 0.4;
      // ⚠ CLAMPED so break-even never exceeds stop-qualified on any one trade —
      // the invariant a real walk guarantees. Without it, scaling a NEGATIVE
      // extension reads as LESS negative than the stop value, which no walk can
      // produce.
      const beOf = (e: number): number => r2(Math.min(beStop ? Math.min(e, 0.15) : e * beScale, e));
      const mkExt = (er: number, beEr: number) => ({
        points: r2(er * riskPts), dollars: r2(er * riskPts * DOLLARS_PER_POINT),
        rMultiple: er, exitReason: extReason, stoppedFirst: wouldStop, qualified: true,
        be: {
          points: r2(beEr * riskPts), dollars: r2(beEr * riskPts * DOLLARS_PER_POINT),
          rMultiple: beEr, stoppedFirst: beStop, qualified: true,
        },
        computedAt: FIXED_TS,
      });

      const hh = 8 + Math.floor(rng() * 6);
      const mm = Math.floor(rng() * 60);
      const ss = Math.floor(rng() * 60);
      const holdS = Math.round(plo(prof.hold));
      const o = hh * 3600 + mm * 60 + ss + holdS;
      const timeIn = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
      const timeOut = `${pad(Math.floor(o / 3600) % 24)}:${pad(Math.floor(o / 60) % 60)}:${pad(o % 60)}`;

      // ⚠ A RECORD, not the old client's array. Confluences are
      // `Record<id, { active }>` in this client's model, and handing a selector
      // an array would give it `.length` where it expects keys.
      const confluences: Record<string, { active: boolean }> = {};
      for (let c = 0; c < 1 + Math.floor(rng() * 3); c++) confluences[pick(CONFLUENCES)] = { active: true };

      trades.push({
        id: DEMO_PREFIX + 'trade_' + tradeNo,
        account_id: DEMO_ACCOUNT_ID,
        date: dateStr,
        session_date: dateStr,
        time_in: timeIn,
        time_out: timeOut,
        contract: 'ES',
        contract_full: esContractFor(dateStr),
        side: long ? 'long' : 'short',
        qty,
        entry,
        exit,
        points,
        gross_pnl: grossPnl,
        fees,
        fees_auto: false,
        net_pnl: netPnl,
        mae_points: maePts,
        mfe_points: mfePts,
        mae_secs: Math.round(holdS * (isWin ? 0.25 : favFirst ? 0.7 : 0.2)),
        mfe_secs: Math.round(holdS * (isWin ? 0.6 : favFirst ? 0.3 : 0.8)),
        risk_points: rt(riskPts),
        rr_planned: 2,
        rr_realized: r2(rr),
        planned_stop: rt(long ? entry - riskPts : entry + riskPts),
        planned_target: rt(long ? entry + riskPts * 2 : entry - riskPts * 2),
        risk_at_entry: r2(riskPts * DOLLARS_PER_POINT * qty),
        pattern_id: DEMO_PREFIX + 'pat_' + prof.sfx,
        exit_model_id: pick(EXIT_MODELS.map(([sfx]) => DEMO_PREFIX + sfx)),
        grade: pick(['A', 'A', 'B', 'B', 'C']),
        session_label: pick(SESSIONS),
        market_regime: '',
        news_event: '',
        mood: pick(MOODS),
        mistake_tags: isWin ? []
          : rng() < 0.5 ? [pick(['chased entry', 'no confirmation', 'moved stop', 'revenge'])] : [],
        confluences,
        booleans: {
          planNotFollowed: rng() < 0.18,
          stopNotHonored: rng() < 0.1,
          targetNotHonored: isWin ? rng() < 0.35 : rng() < 0.7,
          fomoTrade: !isWin && rng() < 0.28,
          revengeTrade: !isWin && rng() < 0.22,
          overtraded: rng() < 0.15,
        },
        controllable_loss_amount: 0,
        screenshots: [],
        recordings: [],
        notes: '',
        // ⚠ ANNOTATED. An unannotated demo book would put 150 sample trades in
        // the trader's review queue and nag them about work that is not theirs.
        annotated: true,
        imported: false,
        source_account: null,
        synthetic_close: false,
        flagged: false,
        flag_reasons: [],
        // ⚠ `extensions` IS NOT A COLUMN — it rides `extras`, exactly as a real
        // trade's does. See `TRADE_COLUMNS`.
        extras: {
          extensions: {
            15: mkExt(ext15, beOf(ext15)),
            30: mkExt(ext30, beOf(ext30)),
            60: mkExt(ext60, beOf(ext60)),
            session: mkExt(extSe, beOf(extSe)),
          },
        },
        created_at: new Date(FIXED_TS + tradeNo).toISOString(),
        updated_at: new Date(FIXED_TS + tradeNo).toISOString(),
      });
    }
  }

  const { accounts, adjustments } = buildPortfolio(todayDate, trades);
  return { accounts, adjustments, patterns, exitModels, trades };
}

// ---------------------------------------------------------------------------
// the prop portfolio
// ---------------------------------------------------------------------------

/**
 * One funded account carrying every trade, surrounded by the evals that produced
 * it.
 *
 * ⚠ THE EVALS CARRY FEES AND STATUS BUT NO TRADES, which is exactly how a real
 * journal looks: you archive a blown eval and its trade history predates the
 * app. Prop economics reads accounts and adjustments rather than trades, so this
 * populates the whole tab — pipeline, cost-per-funded, pass rate, resets,
 * recurring burn, and a firm comparison where one firm is clearly profitable and
 * the other clearly is not.
 *
 * ⚠ The figures are deliberately round and PUBLIC — real Topstep and Apex list
 * prices — so nothing here reads as a claim about anybody's private terms.
 */
function buildPortfolio(
  today: Date, trades: DemoRow[],
): { accounts: DemoRow[]; adjustments: DemoRow[] } {
  // ⚠ Local date parts, not `toISOString()`. The trade loop dates by local
  // parts, and a UTC round-trip would shift these by a day west of Greenwich.
  const dAgo = (back: number): string => {
    const x = new Date(today);
    x.setDate(x.getDate() - back);
    return dateToISO(x);
  };
  let adjNo = 0;
  const adjustments: DemoRow[] = [];
  const adj = (
    accountId: string, type: string, amount: number, back: number, note: string,
  ): void => {
    adjustments.push({
      id: DEMO_PREFIX + 'adj_' + (++adjNo), account_id: accountId,
      date: dAgo(back), type, amount, note,
      created_at: STAMP, updated_at: STAMP,
    });
  };

  const RULES = (over: Record<string, unknown>): Record<string, unknown> => ({
    maxDDType: 'trailing-intraday', maxDDValue: 2000, dailyLossLimit: 1000,
    profitTarget: 3000, consistencyRulePct: 0, consistencyScope: 'always',
    minTradingDays: 0, payoutSchedule: '', payoutsTaken: 0, maxContracts: 5,
    eodFlatRule: false, scalingRules: '', prohibitedProducts: [],
    ...over,
  });

  const EVAL_PASS_ID = DEMO_PREFIX + 'acct_ts_eval';
  const net = trades.reduce((s, t) => s + Number(t['net_pnl'] ?? 0), 0);

  const funded: DemoRow = {
    id: DEMO_ACCOUNT_ID,
    name: 'Topstep 50K — Funded',
    firm: 'Topstep',
    // ⚠ live-funded, not sim-funded: it takes payouts, and it is what makes the
    // pipeline's cost-per-funded and evals-per-funded resolve to a number
    // instead of "no funded account yet".
    account_type: 'live-funded',
    account_size: 50000,
    starting_balance: 50000,
    current_balance: r2(50000 + net),
    status: 'active',
    auto_balance: true,
    external_id: '',
    fee_amount: 149,
    fee_cadence: 'once',
    activated_from: EVAL_PASS_ID,
    archived: false,
    // A live consistency rule, so the card's Consistency line and the dashboard
    // rail both have something real to show. 40% is Topstep's actual figure.
    rules: RULES({
      consistencyRulePct: 40, minTradingDays: 5,
      payoutSchedule: 'Every 5 winning days', payoutsTaken: 2,
    }),
    created_at: STAMP,
    updated_at: STAMP,
  };

  adj(DEMO_ACCOUNT_ID, 'Activation fee', -149, 96, 'Topstep activation fee');
  adj(DEMO_ACCOUNT_ID, 'Payout', -1250, 45, 'First payout');
  adj(DEMO_ACCOUNT_ID, 'Payout', -1880, 12, 'Second payout');

  const evals: {
    id: string; name: string; firm: string; status: string; archived: boolean;
    size: number; fee: number; cadence: string; back: number;
    extra: (id: string) => void;
  }[] = [
    {
      id: EVAL_PASS_ID, name: 'Topstep 50K — Combine', firm: 'Topstep',
      status: 'passed', archived: true, size: 50000, fee: 165, cadence: 'once', back: 118,
      // ⚠ A FREE reset, logged at $0. The COUNT still has to survive — resets
      // per pass is the signal a pass rate hides.
      extra: (id) => { adj(id, 'Reset fee', 0, 110, 'Free reset — first one on the house'); },
    },
    {
      id: DEMO_PREFIX + 'acct_apex_1', name: 'Apex 50K — Eval #1', firm: 'Apex',
      status: 'blown', archived: true, size: 50000, fee: 137, cadence: 'once', back: 100,
      extra: (id) => { adj(id, 'Reset fee', -80, 78, 'Apex reset'); },
    },
    {
      id: DEMO_PREFIX + 'acct_apex_2', name: 'Apex 50K — Eval #2', firm: 'Apex',
      status: 'blown', archived: true, size: 50000, fee: 137, cadence: 'once', back: 62,
      extra: () => {},
    },
    // ⚠ Not archived, and monthly: the only account contributing recurring burn.
    {
      id: DEMO_PREFIX + 'acct_apex_3', name: 'Apex 100K — Eval', firm: 'Apex',
      status: 'active', archived: false, size: 100000, fee: 137, cadence: 'monthly', back: 20,
      extra: () => {},
    },
  ];

  const evalRows: DemoRow[] = evals.map((e) => {
    adj(e.id, 'Eval fee', -e.fee, e.back, e.firm + ' eval fee');
    e.extra(e.id);
    return {
    id: e.id,
    name: e.name,
    firm: e.firm,
    account_type: 'combine',
    account_size: e.size,
    starting_balance: e.size,
    current_balance: e.size,
    status: e.status,
    auto_balance: true,
    external_id: '',
    fee_amount: e.fee,
    fee_cadence: e.cadence,
    archived: e.archived,
    rules: RULES({
      maxDDValue: e.size === 100000 ? 3000 : 2000,
      profitTarget: e.size === 100000 ? 6000 : 3000,
      consistencyRulePct: e.firm === 'Topstep' ? 40 : 30,
    }),
    created_at: STAMP,
    updated_at: STAMP,
    };
  });

  return { accounts: [funded, ...evalRows], adjustments };
}
